import React, { ReactNode } from "react";
import { Box } from "@mui/joy";
import { theme } from "../styles/theme";

/**
 * Converts URLs in text to clickable links
 * @param text The text to process
 * @returns React elements with URLs converted to clickable links
 */
export const linkifyText = (text: string): ReactNode => {
  if (!text) return null;

  // Regular expression to match URLs inside parentheses
  // This pattern matches:
  // 1. An opening parenthesis: \(
  // 2. A URL starting with http:// or https://: (https?:\/\/[^\s\)]+)
  // 3. A closing parenthesis: \)
  const urlInParenthesesRegex = /\((https?:\/\/[^\s\)]+)\)/g;
  
  // Find all matches (URLs inside parentheses) using exec() instead of matchAll()
  // This approach is compatible with ES5 target
  const matches = [];
  let match;
  while ((match = urlInParenthesesRegex.exec(text)) !== null) {
    matches.push(match);
  }
  
  // If no URLs found inside parentheses, return the original text
  if (matches.length === 0) {
    return text;
  }
  
  // Extract the full matches and the URLs (group 1 in each match)
  const fullMatches = matches.map(match => match[0]); // The entire match including parentheses
  const urls = matches.map(match => match[1]); // Just the URL part (group 1)
  
  // Create a regex pattern that will match any of the full matches exactly
  const exactMatchesPattern = new RegExp(fullMatches.map(match => 
    match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'g');
  
  // Split the text by the exact matches
  const parts = text.split(exactMatchesPattern);
  
  // Combine parts and URLs into a single array of React elements
  const result: ReactNode[] = [];
  
  parts.forEach((part, index) => {
    // Add the text part
    if (part) {
      result.push(part);
    }
    
    // Add the URL part (if there is one at this position)
    if (index < urls.length) {
      // Add opening parenthesis as text
      result.push('(');
      
      // Add the URL as a clickable link
      result.push(
        <Box
          key={`url-${index}`}
          component="a"
          href={urls[index]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: "underline",
            color: (theme) => theme.vars.palette.Content.Medium,
            cursor: "pointer",
          }}
        >
          {urls[index]}
        </Box>
      );
      
      // Add closing parenthesis as text
      result.push(')');
    }
  });
  
  return result;
};